import { MatchStatus, TeamCrest } from '@/entities/match';
import type { Match } from '@/entities/match';
import { PredictionType } from '@/entities/prediction';
import type {
  FirstGoalOutcome,
  MatchOutcome,
  Prediction,
} from '@/entities/prediction';
import { RADIUS, Squircle, StaticSquircle } from '@/shared/ui';

import styles from './MatchStatsModal.module.scss';

interface MatchStatsModalProps {
  match: Match;
  onClose: () => void;
}

/**
 * Тот же ярлык типа, что в шторке Прогноза (438:50949) — в макете этой
 * статистики (483:60926) первая строка подписана «победа», но заводить
 * для одного экрана второе имя того же типа не стал: непоследовательность
 * дороже точного совпадения с одной ячейкой мокапа.
 */
const TYPE_LABELS: Record<string, string> = {
  MATCH_OUTCOME: 'Исход матча',
  EXACT_SCORE: 'Голы',
  FIRST_GOAL: 'Первый гол',
  BOTH_TEAMS_TO_SCORE: 'Обе забьют',
  TOTAL_OVER_UNDER: 'Тотал',
};

const OUTCOME_SHORT_LABELS: Record<MatchOutcome, string> = {
  HOME: 'П1',
  DRAW: 'Х',
  AWAY: 'П2',
};

/** Что игрок поставил — по типу Прогноза, форма ответа сервера уже известна. */
function formatGuess(prediction: Prediction, match: Match): string {
  switch (prediction.type) {
    case PredictionType.MATCH_OUTCOME:
      return prediction.outcome ? OUTCOME_SHORT_LABELS[prediction.outcome] : '—';

    case PredictionType.FIRST_GOAL: {
      const labels: Record<FirstGoalOutcome, string> = {
        HOME: match.homeTeam.shortName ?? match.homeTeam.name,
        AWAY: match.awayTeam.shortName ?? match.awayTeam.name,
        NONE: 'Без гола',
      };
      return prediction.firstGoal ? labels[prediction.firstGoal] : '—';
    }

    case PredictionType.EXACT_SCORE: {
      const home =
        prediction.homeScoreGuess === undefined
          ? '—'
          : String(prediction.homeScoreGuess);
      const away =
        prediction.awayScoreGuess === undefined
          ? '—'
          : String(prediction.awayScoreGuess);
      return `${home}:${away}`;
    }

      case PredictionType.BOTH_TEAMS_TO_SCORE:
      return prediction.bothTeamsScoreGuess === undefined
        ? '—'
        : prediction.bothTeamsScoreGuess
          ? 'Да'
          : 'Нет';

    case PredictionType.TOTAL_OVER_UNDER:
      return prediction.totalOverGuess === undefined
        ? '—'
        : prediction.totalOverGuess
          ? 'Да'
          : 'Нет';
  }
}

/**
 * Детали своих Прогнозов на матч (узел 438:51208).
 *
 * Открывается в двух состояниях: у сыгранного матча — что зашло, а что
 * нет; у идущего — что поставлено, исход пока неизвестен. Второе
 * добавлено 09.09.2026: игрок хочет видеть свою ставку в любой момент.
 *
 * Счёт и исход незавершённого матча сервер намеренно НЕ отдаёт
 * (`matches.controller.ts`, «Текущие матчи игрока»), поэтому здесь
 * ничего не додумывается — недостающее просто не показывается.
 */
export function MatchStatsModal({ match, onClose }: MatchStatsModalProps) {
  const totalPoints = match.myPredictions.reduce(
    (sum, p) => sum + p.pointsAwarded,
    0,
  );

  // Сыгранность определяется по СТАТУСУ Прогноза, а не по счёту матча:
  // счёта у неопубликованного результата нет по построению, и по его
  // отсутствию «матч идёт» не отличить от «сервер не прислал поле».
  const isSettled = match.myPredictions.some(
    (prediction) => prediction.status !== 'CONFIRMED',
  );

  return (
    <form
      className={styles.sheet}
      data-testid="match-stats-modal"
      onSubmit={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className={styles.closeRow}>
        <StaticSquircle asChild width={24} height={24} cornerRadius={RADIUS.sm}>
          <button type="button" className={styles.close} aria-label="Закрыть" onClick={onClose}>
            <img className={styles.closeIcon} src="/images/matches/close.svg" alt="" aria-hidden />
          </button>
        </StaticSquircle>
      </div>

      {/* Заголовок называет реальное состояние. «Завершен» на идущем
          матче — не косметика, а неверное утверждение. */}
      <p className={styles.title}>
        {isSettled
          ? 'Завершен'
          : match.status === MatchStatus.LIVE
            ? 'Идёт'
            : 'Ждём результат'}
      </p>

      <div className={styles.teams}>
        <TeamColumn team={match.homeTeam} />
        <span className={styles.versus}>X</span>
        <TeamColumn team={match.awayTeam} />
      </div>

      <p className={styles.statsTitle}>Статистика</p>

      <div className={styles.table}>
        <div className={styles.tableHead}>
          <span className={styles.headType}>Тип прогноза</span>
          <span className={styles.headGuess}>Прогноз</span>
          <span className={styles.headResult}>Исход</span>
        </div>

        <div className={styles.rows}>
          {match.myPredictions.map((prediction) => (
            <div key={prediction.id} className={styles.row}>
              <span className={styles.colType}>{TYPE_LABELS[prediction.type]}</span>
              <span className={styles.colGuess}>{formatGuess(prediction, match)}</span>
              <span className={styles.colResult}>
                {prediction.status === 'WON' || prediction.status === 'VOID_WON' ? (
                  <span className={styles.resultWon}>
                    <img className={styles.resultIcon} src="/images/header/fire.svg" alt="" aria-hidden />
                    {prediction.pointsAwarded}
                  </span>
                ) : prediction.status === 'LOST' ? (
                  <img
                    className={styles.resultLostIcon}
                    src="/images/matches/oops-minus.svg"
                    alt=""
                    aria-hidden
                  />
                ) : null}
              </span>
            </div>
          ))}
        </div>

        {/* Пока не посчитано, «Итого 0» читается как «начислено ноль»,
            хотя начислять ещё нечего. Строки просто нет. */}
        {isSettled ? (
          <div className={styles.total}>
            <span className={styles.colType}>Итого</span>
            <span className={styles.totalPoints}>
              <img className={styles.resultIcon} src="/images/header/fire.svg" alt="" aria-hidden />
              {totalPoints}
            </span>
          </div>
        ) : null}
      </div>

      <Squircle asChild cornerRadius={RADIUS.xl}>
        <button type="submit" className={styles.done}>
          Понял
        </button>
      </Squircle>
    </form>
  );
}

function TeamColumn({ team }: { team: Match['homeTeam'] }) {
  return (
    <div className={styles.team}>
      <StaticSquircle
        width={100}
        height={100}
        cornerRadius={RADIUS.lg}
        className={styles.crest}
      >
        <TeamCrest
          name={team.name}
          logoUrl={team.logoUrl}
          imageClassName={styles.crestImage}
          fallbackClassName={styles.crestFallback}
        />
      </StaticSquircle>
      <p className={styles.teamName}>{team.name}</p>
    </div>
  );
}
